import { TRACK_IDS } from '../config/generation-config';
/** The track after `id`, or null for the last one. */
export function nextTrackOf(id) {
    const i = TRACK_IDS.indexOf(id);
    return TRACK_IDS[i + 1] ?? null;
}
/** The track before `id`, or null for the first one. */
export function prevTrackOf(id) {
    const i = TRACK_IDS.indexOf(id);
    return i > 0 ? (TRACK_IDS[i - 1] ?? null) : null;
}
/** The `qualifyAt` puzzle for `id`, or null when the track has none (Expert). */
export function qualifyAtFor(config, id) {
    return config.tracks.find((t) => t.id === id)?.qualifyAt ?? null;
}
/** The highest ordinal cleared on this track (0 = none). */
export function highestCleared(progress) {
    if (!progress)
        return 0;
    return Math.max(progress.maxPaidOrdinal, progress.ordinal - 1, 0);
}
/** Has the player touched this track at all? */
export function hasPlayed(progress) {
    return highestCleared(progress) > 0 || (progress?.ordinal ?? 1) > 1;
}
/** This track's OWN line: its `qualifyAt` puzzle cleared. False for a track without one. */
function clearedOwnLine(config, id, progress) {
    const at = qualifyAtFor(config, id);
    return at !== null && highestCleared(progress[id]) >= at;
}
/**
 * The rule, CUMULATIVE (WP-78.9, user 2026-09-29): a track is qualified when
 * its own line is cleared OR any later track's line is. A player who skipped
 * Beginner and cleared Intermediate 35 has shown they are past Beginner too.
 *
 * Without this, each card judged only itself and its predecessor, and that
 * player saw TWO "Recommended" cards (Beginner — 30 never cleared — and
 * Expert) with "Recommended after Beginner 30" on the Intermediate they had
 * already finished. Cumulative, at most one card is ever "Recommended".
 */
export function isQualified(config, id, progress) {
    const i = TRACK_IDS.indexOf(id);
    if (qualifyAtFor(config, id) === null)
        return false;
    return TRACK_IDS.slice(i).some((t) => clearedOwnLine(config, t, progress));
}
export function cardReadiness(config, id, progress) {
    // "Recommended after …" first, whatever this track's own progress (user,
    // 2026-09-28: Expert keeps "after Intermediate 35" even when Expert has been
    // played past puzzle 2). Only once the previous track is cleared does this
    // track's own line decide between "Recommended" and no badge.
    const prev = prevTrackOf(id);
    const prevAt = prev ? qualifyAtFor(config, prev) : null;
    if (prev && prevAt !== null && !isQualified(config, prev, progress)) {
        return { kind: 'after', prev, puzzle: prevAt };
    }
    // This track's own line (cumulative — a later track's line counts too), or
    // puzzle 1 for the last track, which has no line.
    const past = qualifyAtFor(config, id) === null
        ? highestCleared(progress[id]) >= 1
        : isQualified(config, id, progress);
    if (past)
        return null;
    return { kind: 'recommended' };
}
export function winMoveUp(config, track, clearedOrdinal, progress) {
    const next = nextTrackOf(track);
    const at = qualifyAtFor(config, track);
    if (!next || at === null)
        return null;
    const cleared = Math.max(highestCleared(progress[track]), clearedOrdinal);
    const withWin = {
        ...progress,
        [track]: { ordinal: cleared + 1, maxPaidOrdinal: cleared },
    };
    const badge = cardReadiness(config, next, withWin);
    if (!badge)
        return null;
    return { next, active: badge.kind === 'recommended', puzzle: at };
}
